/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { catchError, distinctUntilChanged, filter, map, share, switchMap, take } from 'rxjs/operators';
import { combineLatest, EMPTY, Observable, of, Subscription } from 'rxjs';
import { isEqual } from 'lodash-es';
import { TrackingIntegrationService } from '@nimbus-library/features/tracking/services/tracking-integration.service';
import { PAGE_USAGE_HOST } from '@nimbus-library/features/tracking/models/page-usage-host.constants';
import { AuthService } from '../auth.service';
import { SettingsService } from '../settings.service';

@Injectable({ providedIn: 'root' })
export class TrackingAssistantService {
  public constructor(
    private readonly trackingIntegrationService: TrackingIntegrationService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly settingsService: SettingsService,
  ) {}

  public async initialize(): Promise<Subscription> {
    const subscriptions$ = new Subscription();
    const initializeTracking$ = this.initializeTracking$();
    subscriptions$.add(initializeTracking$.subscribe());
    subscriptions$.add(
      this.authService.authenticated$
        .pipe(
          distinctUntilChanged(),
          switchMap((isAuthenticated) => (isAuthenticated ? this.checkForPageUsageUpdate$() : EMPTY)),
        )
        .subscribe(),
    );

    return subscriptions$;
  }

  private initializeTracking$(): Observable<boolean> {
    return combineLatest([this.authService.authenticated$, this.authService.profile$, this.settingsService.settings$]).pipe(
      distinctUntilChanged(isEqual),
      switchMap(([isAuthenticated, profile, settings]) => {
        if (!isAuthenticated || !profile || !settings?.tenant?.hasPlanhatEnabled || !settings?.tenant?.crmId) {
          this.trackingIntegrationService.stop();
          return of(false);
        }
        const userDetails = settings?.tenant?.allowToSharePii
          ? { id: profile.userPrincipalName, name: profile.displayName }
          : { id: settings?.userHash, name: settings?.userHash };

        return this.trackingIntegrationService.start({ id: settings.tenant.crmId, name: settings.tenant.name }, userDetails, null);
      }),
      share(),
    );
  }

  private checkForPageUsageUpdate$(): Observable<void> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => ({ module: this.getModule(), routeUrl: this.getRouteTemplate() })),
      filter((navigation) => !!navigation.module && !!navigation.routeUrl),
      distinctUntilChanged(isEqual),
      switchMap((navigation) =>
        // The Assistant authenticates API calls via the X-Nimb-Auth header (added by ApiInterceptor),
        // not a same-origin cookie. Since the page usage POST bypasses the interceptor chain, the
        // token has to be acquired and attached explicitly so the request is authenticated.
        this.authService.acquireIdToken().pipe(
          take(1),
          map((idToken) => {
            this.trackingIntegrationService.trackPageUsage(navigation.module, navigation.routeUrl, {
              tenantId: this.authService.getTenantId(),
              userId: this.authService.profile$.value?.id,
              crmId: this.settingsService.settings$.value?.tenant?.crmId,
              host: PAGE_USAGE_HOST.standalone,
              requestHeaders: { 'X-Nimb-Auth': `Bearer ${idToken}` },
            });
          }),
          catchError(() => EMPTY),
        ),
      ),
    );
  }

  private getModule(): string {
    let routeChild = this.activatedRoute.firstChild;

    while (routeChild) {
      const trackingModule = routeChild.snapshot.data?.['trackingModule'];
      if (trackingModule) {
        return trackingModule;
      }

      routeChild = routeChild.firstChild;
    }

    return null;
  }

  private getRouteTemplate(): string {
    const routePaths = [];
    let routeChild = this.activatedRoute.firstChild;

    while (routeChild) {
      const routePath = routeChild.snapshot.routeConfig?.path;
      if (routePath) {
        routePaths.push(routePath);
      }

      routeChild = routeChild.firstChild;
    }

    return routePaths.length ? `/${routePaths.join('/')}` : null;
  }
}
